import { Button, CILabel, Dropdown, Input, Pagination } from 'ccs-ui-components';
import { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { isEmojiInput } from '../../../../../../common/utils/CommonUtils';
import { FIELD_CONDITION_OPTIONS, FIELD_DEF_LIST_CONSTANTS } from '../../../../../constants';
import { usePatternRegistryForm } from '../../../../../hooks';
import { IFieldDefList, IFieldDefRowItem } from '../../../../../models';
import { clearFormDataDynamic, fieldNErrorRetainer, fieldNHandlerOnSelect, fieldNHideShow, handler, isFieldNGreaterHandler, isFieldNGreaterUseEffect, pageFieldDefListSync, pageSync, setFieldNInView, updateCurrentPageList } from '../../../../../utilities/sources/PatternRegistryUtil';
import FieldDefinitionComponent from '../field-definition-component-form/FieldDefinitionComponentForm';
import FieldDefListRowItem from './FieldDefinitionListRowItem';
import FieldDefinitionHeader from './FieldDefinitionHeader';

import classes from '../PatternRegistryForm.module.scss';

const FieldDefList = (props: IFieldDefList) => {
  const { data, deleteHandler, formData, updateFieldCondition, fieldCondition, handleInputChange, editHandler, disabled, pageNumber, updatePageNumber, setFormParamValues, isView, isEdit, updateList, fieldEnabler, isEditFD, isEditPatternMode, isViewPatternMode, setEditHandler, setCurrentEditId, currentEditId } = props;
  const { getFormParam } = usePatternRegistryForm();
  const { t: translate } = useTranslation();

  const [page, setPage] = useState(0);
  const [item, setItem] = useState(5);
  const [listData, setListData] = useState<IFieldDefRowItem[]>([]);
  const [valueError, setValueError] = useState('');
  const [noOfTimes, setNoOfTimes] = useState('');
  const [oldData, setOldData] = useState<IFieldDefRowItem[]>([]);
  const [showFieldDefInput, setShowFieldDefInput] = useState(true);
  const [showTable, setShowTable] = useState(false);

  /**
   * @method selectHandler
   * @description handle field condition select
   * @param e change event
   */
  const selectHandler = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const errString = `${translate('pattern_registry_form.fieldN_error', { replace: { maxChars: data.length } })}`;
    fieldNHandlerOnSelect(e, setFormParamValues, formData, data, setValueError, errString);
    updateFieldCondition(e.target.value);
    if (formData.fieldCondition.value === FIELD_DEF_LIST_CONSTANTS.ALL || formData.fieldCondition.value === FIELD_DEF_LIST_CONSTANTS.ANY) clearFormDataDynamic(formData, [formData.fieldN.name]);
  };

  /**
   * @method fieldNChangeHandler
   * @description Handles the change event for the fieldN input element.
   * @param e - The change event object.
   */
  const fieldNChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    // value should not be greater than data length
    if (isEmojiInput(e.target.value)) {
      return;
    }
    if (isFieldNGreaterHandler(e, formData, data)) {
      setFormParamValues((previousData) => {
        const newFormData = structuredClone(previousData);
        newFormData.fieldN.isTouched = true;
        newFormData.fieldN.isValid = false;
        newFormData.fieldN.error = '';
        return newFormData;
      });
      setValueError(`${translate('pattern_registry_form.fieldN_error', { replace: { maxChars: data.length } })}`);
    } else {
      setValueError('');
    }
    handleInputChange(e);
    setNoOfTimes(e.target.value);
  };

  /**
   * @method onPageNoUpdate
   * @description updates current page in parent and in current state
   * @param pageNo: number
   */
  const onPageNoUpdate = (pageNo: number) => {
    setPage(pageNo);
    updatePageNumber(pageNo);
  };

  /**
   * @method getItemPerPage
   * @description update the items per page state
   * @param items: number
   */
  const getItemPerPage = (items: number) => {
    setItem(items);
  };

  /**
   * update page state when pageNumber is received from parent
   */
  useEffect(() => {
    setPage(pageNumber);
  }, [pageNumber]);

  /**
   * manipulate the list to show only 3 items per page
   * Reference: ServiceException logic
   */
  useEffect(() => {
    // sync the page number
    pageSync(data, onPageNoUpdate, item, page);
    // update the list based on items per page
    updateCurrentPageList(data, item, page, setListData);
    // to handle scenarios when error already occurred and data length changes
    if (isFieldNGreaterUseEffect(formData, data)) {
      handler(data, oldData, setFormParamValues, setNoOfTimes, setValueError, '');
    } else if (data.length < 1) {
      setFormParamValues((previousData) => {
        const newFormData = structuredClone(previousData);
        newFormData.fieldN.isValid = true;
        newFormData.fieldN.error = '';
        return newFormData;
      });
      setNoOfTimes('');
      setValueError('');
    } else {
      setNoOfTimes(formData.fieldN.value);
    }
  }, [data, item, page, isView]);

  /**
   * this helps us clear the # Of Field Definitions when a new record is created or deleted
   */
  useEffect(() => () => {
    // sync oldData with current listData
    pageFieldDefListSync(oldData, listData, isView, setOldData); // don't do this in view mode
    if (!isEdit && !isView) {
      setNoOfTimes('');
      setValueError('');
      setFormParamValues((previousData) => {
        const newFormData = structuredClone(previousData);
        newFormData.fieldN.value = '';
        newFormData.fieldN.error = '';
        newFormData.fieldN.isValid = true;
        return newFormData;
      });
    }
  }, [listData, oldData]);

  /**
  * this useEffect is used to populate fieldN value(if it exists) as soon we create a pattern
  */
  useEffect(() => {
    setFieldNInView(formData, setNoOfTimes, isView);
  }, [formData, isView]);

  /**
   * if any error exist then clear it on addition of new field definition
   */
  useEffect(() => {
    fieldNErrorRetainer(valueError, noOfTimes, isView, setFormParamValues);
  }, [valueError, noOfTimes, isView]);

  const addNewDefHandler = () => {
    if (showFieldDefInput === false) {
      setShowTable(true);
      setShowFieldDefInput(true);
    } else setShowTable(!showTable);
  };

  useEffect(() => {
    if (isEdit) {
      setShowTable(true);
      setShowFieldDefInput(false);
    }
  }, [isEdit]);

  useEffect(() => {
    if (isView) {
      setShowTable(true);
      setShowFieldDefInput(false);
    }
  }, [isView]);

  return (
    <>
      <Form
        className={classes['form-container']}
        data-testid="pr-field-condition"
      >
        <div className={classes['occurrences-form-header']}>
          <div className={classes['occurrences-header-title']}>{translate('pattern_registry_form.lbl_field_definition_name')}</div>
          <div className={classes['field-definition-sub-title']}>
            {translate('pattern_registry_form.lbl_field_def_sub_title')}
          </div>
          <p className={classes['field-definition-desc']}>
            {translate('pattern_registry_form.lbl_field_def_desc')}
          </p>
        </div>
        <Row className={classes['pr-field-condition-row']}>
          <Col sm={4}>
            <Form.Group
              className={`${classes['form-grp']} ${classes['pr-form-grp-flex']}`}
            >
              <CILabel className={`${classes['form-label']} ${classes['pr-form-label-row']}`} id="fieldCondition" isMandatory labelText={translate('pattern_registry_form.lbl_field_condition_name')} />
              <div className={classes['pr-first-div']}>
                <Dropdown
                  label=""
                  data={FIELD_CONDITION_OPTIONS}
                  value={fieldCondition}
                  onChange={selectHandler}
                  id="fieldCondition"
                  name="fieldCondition"
                  data-testid="fieldConditionTest"
                  error={!formData.fieldCondition.isValid}
                  className={classes['cp-pr-fixed-width-select']}
                  disabled={isView}
                  placeholder={String(translate('fsm_registry_form.lbl_placeholder_select'))}
                />
                <div className={classes['cp-pr-error-container']}>
                  {!formData.fieldCondition.isValid
                    && formData.fieldCondition.isTouched && (
                      <h6 className={classes['cp-pr-error']} data-testid="pattern-name-error-text">
                        {formData.fieldCondition.error}
                      </h6>
                    )}
                </div>
              </div>
            </Form.Group>
          </Col>
          <Col sm={3}>
            {data.length > 0 && (
              <Form.Group
                className={`${classes['form-grp']} ${classes['pr-form-grp-flex']}`}
              >
                <CILabel className={`${classes['form-label']} ${classes['pr-form-label-row']} ${fieldNHideShow(fieldCondition)}`} id="fieldN" isMandatory labelText={translate('pattern_registry_form.lbl_of_times_name')} />
                <div className={`${classes['pr-first-div']} ${fieldNHideShow(fieldCondition)}`}>
                  <Input
                    type="text"
                    placeholder={translate('pattern_registry_form.plchd_fd_number') ?? ''}
                    label=""
                    id="fieldN"
                    name="fieldN"
                    value={noOfTimes}
                    onChange={fieldNChangeHandler}
                    error={(!formData.fieldN.isValid || valueError !== '') && !isView}
                    data-testid="pr-fieldNTest"
                    disabled={isView}
                  />
                  <div className={classes['cp-pr-error-container']}>
                    {!formData.fieldN.isValid
                      && formData.fieldN.isTouched && !isView && (
                        <h6 className={`${classes['cp-pr-error']} ${classes['cp-pr-fieldN']}`} data-testid="pattern-name-error-text">
                          {formData.fieldN.error}
                        </h6>
                      )}
                    {valueError !== '' && !isView && (
                      <h6 className={`${classes['cp-pr-error']} ${classes['cp-pr-fieldN-maxchar']}`} data-testid="pr-max-value">
                        {valueError}
                      </h6>
                    )}
                  </div>
                </div>
              </Form.Group>
            )}
          </Col>
          {!isView && (
            <Col sm={5}>
              <Form.Group>
                <Button variant="primary" className={classes['cp-pr-addDefBtn']} onClick={addNewDefHandler} data-testid="addNewDefBtn">
                  {translate('pattern_registry_form.addNewDefinitionBtn')}
                </Button>
              </Form.Group>
            </Col>
          )}
        </Row>
      </Form>
      {showTable && (
        <div className={classes['pr-list-container']}>
          <div className={classes['pattern-list']} data-testid="pattern-list">
            <FieldDefinitionHeader />
            {!currentEditId && <FieldDefinitionComponent
              formData={formData}
              handleInputChange={handleInputChange}
              setFormParamValues={setFormParamValues}
              getFormParam={getFormParam}
              updateList={updateList}
              fieldEnabler={fieldEnabler}
              isEditFD={isEditFD}
              isEditPatternMode={isEditPatternMode}
              isViewPatternMode={isViewPatternMode}
              setEditHandler={setEditHandler}
              data={data}
              setShowFieldDefInput={setShowFieldDefInput}
              showFieldDefInput={showFieldDefInput}
              currentEditId={currentEditId}
              setCurrentEditId={setCurrentEditId}
            />}
            {listData.map((item: IFieldDefRowItem) => ((item.id === currentEditId) ? (<FieldDefinitionComponent
              formData={formData}
              key={item.id}
              handleInputChange={handleInputChange}
              setFormParamValues={setFormParamValues}
              getFormParam={getFormParam}
              updateList={updateList}
              fieldEnabler={fieldEnabler}
              isEditFD={isEditFD}
              isEditPatternMode={isEditPatternMode}
              isViewPatternMode={isViewPatternMode}
              setEditHandler={setEditHandler}
              data={data}
              setShowFieldDefInput={setShowFieldDefInput}
              showFieldDefInput={showFieldDefInput}
              setCurrentEditId={setCurrentEditId}
              currentEditId={currentEditId}
            />) : (<FieldDefListRowItem
              data={item}
              key={item.id}
              deleteItem={deleteHandler}
              editItem={editHandler}
              disabled={disabled}
              isView={isView}
              isEdit={false}
              setShowFieldDefInput={setShowFieldDefInput}
            />)
            ))}

            {listData.length > 0 && (
              <div className={classes['pr-pagination-styling']} data-testid="pr-fd-list-pagination">
                <Pagination
                  selectItems={[5, 10]}
                  itemsPerPage={item}
                  currentPageNumber={page}
                  numberOfPages={Math.ceil(data.length / item)}
                  onCurrentPageNumberChange={onPageNoUpdate}
                  onItemsPerPageChange={getItemPerPage}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
    // ) : null
  );
};

export default FieldDefList;
